import React from "react";
import Layout from "../../components/Layout";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Divider,
  Flex,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { Routing } from "../../constants/routing";
import { translations } from "../../i18n/translations";
import { PresentationProps } from "./Job.types";
import { Link as RouterLink } from "react-router-dom";
import { getAddressCopy } from "../../utils/Job/getAddressCopy";
import { JobDataTable } from "./components/JobDataTable";
import { PropertyContacts } from "./components/PropertyContacts";
import { JobReport } from "./components/JobReport";
import { PropertyDetails } from "./components/PropertyDetails";
import { FiChevronDown as ChevronDownIcon } from "react-icons/fi";
import FullPageContent from "../../components/FullPageContent";
import { getTagColor } from "../../utils/Job/getTagColor";
import { getServiceTypeCopy } from "../../utils/Job/getServiceTypeCopy";

export const JobPresentation = (props: PresentationProps) => {
  const screen = translations.screens.job;
  const {
    assignJobModal,
    cancelJobModal,
    pageTitleRef,
    breadcrumbsRef,
    isLoading,
    job,
    statics,
  } = props;

  return (
    <Layout>
      <Portal containerRef={pageTitleRef}>{t<string>(screen.pageTitle)}</Portal>
      <Portal containerRef={breadcrumbsRef}>
        <Breadcrumb fontWeight={500}>
          <BreadcrumbItem color="gray.400">
            <BreadcrumbLink as={RouterLink} to={Routing.Root}>
              {t<string>(screen.breadcrumbs.root)}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to={Routing.Jobs}>
              {t<string>(screen.breadcrumbs.jobs)}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <div>{t<string>(screen.breadcrumbs.job)}</div>
          </BreadcrumbItem>
        </Breadcrumb>
      </Portal>
      <FullPageContent>
        <Link
          _focus={{ boxShadow: "none" }}
          as={RouterLink}
          fontSize={12}
          fontWeight={500}
          style={{ textDecoration: "none" }}
          to={Routing.Jobs}
        >
          {t<string>(screen.backToJobs)}
        </Link>
        {isLoading && (
          <Flex justifyContent="center" mt={4}>
            <Spinner />
          </Flex>
        )}
        {!isLoading && (
          <>
            <br />
            <br />
            <Flex justifyContent="space-between">
              <Text fontSize="lg" fontWeight={700}>
                {job && getAddressCopy(job)}
              </Text>
              <Flex>
                <Menu>
                  <MenuButton
                    as={Button}
                    colorScheme="blue"
                    rightIcon={<ChevronDownIcon />}
                    size="sm"
                  >
                    {t<string>(screen.manageJob)}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={assignJobModal.onOpen}>
                      {t<string>(screen.reassignJob)}
                    </MenuItem>
                    {job?.jobStatus !== "Cancelled" && (
                      <MenuItem onClick={cancelJobModal.onOpen}>
                        {t<string>(screen.cancelJob)}
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              </Flex>
            </Flex>
            <Flex mb={8} mt={1}>
              {(job?.serviceTypes?.length ?? 0) > 0 && (
                <Flex flexWrap="wrap" gap={1}>
                  {job?.serviceTypes
                    .sort((a, b) => a.id - b.id)
                    .slice(0, 2)
                    .map((serviceType) => (
                      <Tag
                        key={serviceType.id}
                        colorScheme={getTagColor(serviceType.id)}
                      >
                        <Text size="12px">
                          {
                            getServiceTypeCopy(serviceType.id, statics)
                              .serviceType
                          }
                        </Text>
                      </Tag>
                    ))}
                  {(job?.serviceTypes?.length ?? 0) > 2 && (
                    <Tag colorScheme="gray">
                      <Text size="12px">
                        +{(job?.serviceTypes.length ?? 0) - 2}
                      </Text>
                    </Tag>
                  )}
                </Flex>
              )}
            </Flex>
            <Divider />
            <Flex
              alignItems="stretch"
              flexDirection={["column", "column", "column", "row"]}
              gap="18px"
            >
              <Flex pt={5} width={["100%", "100%", "100%", "70%"]}>
                <Tabs width="100%">
                  <TabList>
                    <Tab>{t<string>(screen.jobDetails)}</Tab>
                    <Tab>{t<string>(screen.propertyInformation)}</Tab>
                    <Tab>{t<string>(screen.jobReport)}</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <Flex mb={1} mt={3}>
                        <Text fontSize="sm" fontWeight={600}>
                          {t<string>(screen.jobDetails)}
                        </Text>
                      </Flex>
                      <JobDataTable job={job} statics={statics} />
                    </TabPanel>
                    <TabPanel>
                      <PropertyDetails job={job} statics={statics} />
                    </TabPanel>
                    <TabPanel>
                      <JobReport
                        job={job}
                        jobId={job?.jobId}
                        status={job?.jobStatus}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Flex>
              <Flex
                borderColor="gray.200"
                borderLeftWidth="1px"
                pl={5}
                pt={5}
                width={["100%", "100%", "100%", "30%"]}
              >
                <PropertyContacts data={job?.propertyContacts || []} />
              </Flex>
            </Flex>
          </>
        )}
      </FullPageContent>
    </Layout>
  );
};
